import {
  REVENUE_CLOSE_PERMISSION,
  REVENUE_LEDGER_RUN_TIME_JST,
} from '@cortex/domain-ac-mens-hub-api';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { LineChart as LineChartIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../hooks/usePermissions.js';
import {
  useLedgerChecks,
  useLedgerClosings,
  useLedgerDaily,
  useLedgerDefinitions,
  useLedgerMonthly,
} from '../../../hooks/useRevenueLedger.js';
import { LedgerBalanceView } from './LedgerBalanceView.js';
import { type LedgerDailyStatus, LedgerChartView } from './LedgerChartView.js';
import { LedgerChecksView } from './LedgerChecksView.js';
import { LedgerDefinitionView } from './LedgerDefinitionView.js';
import { type LedgerClosingsState, LedgerMonthlyView } from './LedgerMonthlyView.js';
import { PendingMonthsNote } from './PendingMonthsNote.js';
import { RevenueCompareView } from './RevenueCompareView.js';

/**
 * 売上ページのタブ（チャート/月次/前受金・貸倒/版の比較/要対応/定義）。
 * @graph-connects none
 */
export type RevenueViewMode =
  | 'chart'
  | 'monthly'
  | 'deferred'
  | 'compare'
  | 'validation'
  | 'definition';

/**
 * 取得中・失敗・成功の出し分け。
 * @graph-connects none
 */
function QueryState<T>({
  query,
  errorKey,
  children,
}: {
  query: { data?: T; isLoading: boolean; isError: boolean };
  errorKey: string;
  children: (data: T) => ReactNode;
}) {
  const { t } = useTranslation();
  if (query.isLoading) {
    return <p className="mt-8 text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>;
  }
  if (query.isError || query.data === undefined) {
    return (
      <Card className="mt-6 p-6">
        <p className="text-sm text-[var(--color-text-secondary)]">{t(errorKey)}</p>
      </Card>
    );
  }
  return <>{children(query.data)}</>;
}

/**
 * 日次の取得の状況を、チャートに渡す状態にする（失敗・まだ値が無い間・日の範囲の指定が逆で取得しない間を
 * 空として渡さない）。
 * @graph-connects none
 */
function dailyStatusOf(
  query: { data?: unknown; isError: boolean },
  validDays: boolean,
): LedgerDailyStatus {
  if (!validDays) return 'invalidRange';
  if (query.isError) return 'error';
  return query.data === undefined ? 'loading' : 'ready';
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上ページで正式版（v0.1）を選んだときの中身。タブごとに定義の版つき台帳・日次・締めの状態・要対応・定義を読み、版の台帳が空なら「まだ実行されていません」と毎朝の更新時刻を、台帳はあるが期間に値が無ければ「この期間に値はありません」と台帳の最初の月を出し、前日を基準日とする計算がまだ無い当月はその旨を伝える。締めの権限は読み込みが終わるまで持っていないとみなし、締めの状態・日次の読み込み中・失敗は空として描かない。
 * @graph-connects useLedgerMonthly [calls] 版つき台帳の月次
 * @graph-connects useLedgerDaily [calls] 日次の前日からの増加
 * @graph-connects useLedgerClosings [calls] 締めの状態
 * @graph-connects useLedgerChecks [calls] 要対応
 * @graph-connects useLedgerDefinitions [calls] 定義の版
 * @graph-connects usePermissions [calls] 締めの権限の判定
 * @graph-connects LedgerChartView [calls] チャートタブ
 * @graph-connects LedgerMonthlyView [calls] 月次タブ
 * @graph-connects LedgerBalanceView [calls] 前受金・貸倒タブ
 * @graph-connects RevenueCompareView [calls] 版の比較タブ
 * @graph-connects PendingMonthsNote [calls] 当月の値をまだ出さない月の注記
 * @graph-connects LedgerChecksView [calls] 要対応タブ
 * @graph-connects LedgerDefinitionView [calls] 定義タブ
 */
export function RevenueLedgerPanel({
  viewMode,
  from,
  toDate,
  fromMonth,
  toMonth,
  monthOptions,
  includeWithdrawal,
}: {
  viewMode: RevenueViewMode;
  from: string;
  toDate: string;
  fromMonth: string;
  toMonth: string;
  monthOptions: readonly string[];
  includeWithdrawal: boolean;
}) {
  const { t } = useTranslation();
  const validMonths = fromMonth <= toMonth;
  const validDays = from <= toDate;
  const ledgerQuery = useLedgerMonthly({ version: 'v0.1', fromMonth, toMonth }, validMonths);
  const dailyQuery = useLedgerDaily(from, toDate, viewMode === 'chart' && validDays);
  const closingsQuery = useLedgerClosings(
    fromMonth,
    toMonth,
    viewMode === 'monthly' && validMonths,
  );
  const checksQuery = useLedgerChecks(viewMode === 'validation');
  const definitionsQuery = useLedgerDefinitions(viewMode === 'definition');
  // 権限の読み込み中は「持っていない」とみなしてボタンを出さない。状態に確定させず毎回導出する
  const permissions = usePermissions();
  const canClose = !permissions.isLoading && permissions.hasPermission(REVENUE_CLOSE_PERMISSION);
  const closingsState: LedgerClosingsState =
    closingsQuery.data === undefined
      ? { status: closingsQuery.isError ? 'error' : 'loading' }
      : { status: 'ready', closings: closingsQuery.data.closings };

  if (viewMode === 'compare') {
    return <RevenueCompareView monthOptions={monthOptions} defaultMonth={toMonth} />;
  }
  if (viewMode === 'validation') {
    return (
      <QueryState query={checksQuery} errorKey="revenue.checks.fetchError">
        {(data) => <LedgerChecksView data={data} />}
      </QueryState>
    );
  }
  if (viewMode === 'definition') {
    return (
      <QueryState query={definitionsQuery} errorKey="revenue.definitions.fetchError">
        {(data) => <LedgerDefinitionView definitions={data.definitions} />}
      </QueryState>
    );
  }
  // 開始月が終了月より後だと台帳を取りに行かない。取得の失敗と区別して、期間の指定を直すよう伝える
  if (!validMonths) {
    return (
      <Card className="mt-6 p-6">
        <p role="alert" className="text-sm text-[var(--color-text-secondary)]">
          {t('revenue.ledger.invalidRange')}
        </p>
      </Card>
    );
  }
  return (
    <QueryState query={ledgerQuery} errorKey="revenue.ledger.fetchError">
      {(ledger) => {
        // 未実行と出すのは版の台帳が空のときだけ。期間に値が無いだけなら、台帳の最初の月を添えて出し分ける
        if (
          ledger.run === null ||
          (ledger.rows.length === 0 && ledger.pendingMonths.length === 0)
        ) {
          return (
            <Card className="mt-6 p-8 text-center">
              <LineChartIcon
                className="mx-auto h-8 w-8 text-[var(--color-text-muted)]"
                aria-hidden="true"
              />
              <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
                {ledger.run === null
                  ? t('revenue.ledger.empty', { time: REVENUE_LEDGER_RUN_TIME_JST })
                  : t('revenue.ledger.noValuesInPeriod', {
                      version: ledger.version,
                      firstMonth: ledger.run.firstMonth,
                    })}
              </p>
            </Card>
          );
        }
        // 当月の値をまだ出さない月は、空の列を未実行と取り違えないよう伝える
        const pendingNote = <PendingMonthsNote months={ledger.pendingMonths} className="mt-6" />;
        if (viewMode === 'monthly') {
          return (
            <>
              {pendingNote}
              <LedgerMonthlyView
                ledger={ledger}
                closingsState={closingsState}
                canClose={canClose}
                includeWithdrawal={includeWithdrawal}
              />
            </>
          );
        }
        if (viewMode === 'deferred') {
          return (
            <>
              {pendingNote}
              <LedgerBalanceView ledger={ledger} />
            </>
          );
        }
        return (
          <>
            {pendingNote}
            <LedgerChartView
              monthlyRows={ledger.rows}
              dailyRows={dailyQuery.data?.rows ?? []}
              dailyStatus={dailyStatusOf(dailyQuery, validDays)}
              firstDate={dailyQuery.data?.firstDate ?? null}
              referenceThrough={dailyQuery.data?.referenceThrough ?? null}
              includeWithdrawal={includeWithdrawal}
            />
          </>
        );
      }}
    </QueryState>
  );
}
